import { createContext, useContext, useState, useCallback, useEffect } from 'react'
import { createPortal } from 'react-dom'

// ── Module-level dispatch so toast() works outside React components ───────────
let _dispatch = null
function setDispatch(fn) { _dispatch = fn }

export const toast = {
  success: (msg) => _dispatch?.({ type: 'success', msg }),
  error:   (msg) => _dispatch?.({ type: 'error',   msg }),
  info:    (msg) => _dispatch?.({ type: 'info',     msg }),
}

// ── React context (optional — use toast.* directly in most cases) ─────────────
const ToastContext = createContext(null)
export function useToast() { return useContext(ToastContext) }

let _nextId = 0

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([])

  const add = useCallback((type, msg) => {
    const id = ++_nextId
    setToasts(prev => [...prev, { id, type, msg }])
    const duration = type === 'error' ? 6000 : 4000
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), duration)
  }, [])

  const remove = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id))
  }, [])

  useEffect(() => {
    setDispatch(({ type, msg }) => add(type, msg))
    return () => setDispatch(null)
  }, [add])

  const icons = {
    success: (
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
        <circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1.4"/>
        <path d="M4 7l2 2 4-4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    error: (
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
        <circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1.4"/>
        <path d="M5 5l4 4M9 5l-4 4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
      </svg>
    ),
    info: (
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
        <circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1.4"/>
        <path d="M7 6.5v3M7 4.5v.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    ),
  }

  return (
    <ToastContext.Provider value={{ add, remove }}>
      {children}
      {createPortal(
        <div className="toast-stack">
          {toasts.map(t => (
            <div key={t.id} className={`toast-item toast-${t.type}`}>
              <span className="toast-icon">{icons[t.type]}</span>
              <span className="toast-msg">{t.msg}</span>
              <button className="toast-close" onClick={() => remove(t.id)} aria-label="Dismiss">
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M2 2l8 8M10 2l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </button>
            </div>
          ))}
        </div>,
        document.body
      )}
    </ToastContext.Provider>
  )
}
